import type { FC, FormEvent, ReactNode } from 'react'
import type { SearchResponse } from '@/components/search/search-types'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { ArrowRight, Loader2, Search, X } from 'lucide-react'
import Highlight from '@/components/search/highlight'
import InteriorAnswer from '@/components/search/interior-answer'

import { GROUP_ORDER, POPULAR_SEARCHES } from '@/components/search/search-types'

interface SearchDialogProps {
    open: boolean
    onClose: () => void
}

export const logSearch = (query: string, results: number, clicked?: string): void => {
    if (query.trim().length < 2) return

    try {
        void fetch('/api/search-log/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query, results, clicked: clicked ?? null }),
            keepalive: true
        })
    }
    catch {
        // Logging must never get in the visitor's way.
    }
}

const SearchDialog: FC<SearchDialogProps> = ({ open, onClose }): ReactNode => {
    const router = useRouter()
    const inputRef = useRef<HTMLInputElement>(null)
    const loggedRef = useRef<string>('')
    const [query, setQuery] = useState('')
    const [data, setData] = useState<SearchResponse | null>(null)
    const [loading, setLoading] = useState(false)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (!open) return

        const previous = document.activeElement as HTMLElement | null
        const timer = window.setTimeout(() => inputRef.current?.focus(), 30)

        document.body.style.overflow = 'hidden'

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        window.addEventListener('keydown', onKey)

        return () => {
            window.clearTimeout(timer)
            window.removeEventListener('keydown', onKey)
            document.body.style.overflow = ''
            previous?.focus?.()
        }
    }, [open, onClose])

    useEffect(() => {
        if (!open) return

        const trimmed = query.trim()

        if (trimmed.length < 2) {
            setData(null)
            setLoading(false)
            setFailed(false)
            return
        }

        const controller = new AbortController()
        const timer = window.setTimeout(async () => {
            setLoading(true)
            setFailed(false)

            try {
                const response = await fetch(`/api/search/?q=${encodeURIComponent(trimmed)}&limit=12`, { signal: controller.signal })

                if (!response.ok) throw new Error('Search failed')

                setData(await response.json() as SearchResponse)
            }
            catch (error) {
                if ((error as Error).name !== 'AbortError') setFailed(true)
            }
            finally {
                setLoading(false)
            }
        }, 220)

        return () => {
            window.clearTimeout(timer)
            controller.abort()
        }
    }, [query, open])

    useEffect(() => {
        if (!data || data.total > 0 || loggedRef.current === data.query) return

        const timer = window.setTimeout(() => {
            loggedRef.current = data.query
            logSearch(data.query, 0)
        }, 1500)

        return () => window.clearTimeout(timer)
    }, [data])

    if (!open) return null

    const submit = (event: FormEvent) => {
        event.preventDefault()

        const trimmed = query.trim()

        if (!trimmed) return

        onClose()
        void router.push(`/search/?q=${encodeURIComponent(trimmed)}`)
    }

    const choose = (url: string) => {
        logSearch(query, data?.total ?? 0, url)
        onClose()
    }

    const grouped = GROUP_ORDER
        .map(group => ({ group, items: data?.results.filter(result => result.group === group) ?? [] }))
        .filter(entry => entry.items.length > 0)

    return (
        <div className='fixed inset-0 z-[80] flex items-start justify-center bg-navy-deep/60 p-3 backdrop-blur-sm sm:p-6' onMouseDown={onClose}>
            <div
                role='dialog'
                aria-modal='true'
                aria-label='Search the site'
                className='mt-2 flex max-h-[calc(100svh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-background shadow-2xl animate-in fade-in slide-in-from-top-4 duration-300 sm:mt-10'
                onMouseDown={event => event.stopPropagation()}
            >
                <form onSubmit={submit} className='flex items-center gap-3 border-b border-border p-4 sm:p-5' role='search'>
                    <label htmlFor='site-search-input' className='sr-only'>
                        Search the site
                    </label>

                    <Search className='size-6 shrink-0 text-navy' aria-hidden='true' />

                    <input
                        ref={inputRef}
                        id='site-search-input'
                        type='search'
                        name='q'
                        autoComplete='off'
                        enterKeyHint='search'
                        placeholder='Search windows, doors, siding, towns...'
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        className='min-h-14 w-full min-w-0 rounded-xl bg-white px-4 text-lg text-foreground outline-none ring-2 ring-border focus:ring-primary'
                    />

                    <button
                        type='button'
                        onClick={onClose}
                        className='inline-flex min-h-14 shrink-0 items-center gap-2 rounded-xl px-4 font-bold text-navy hover:bg-muted'
                    >
                        <X className='size-5' aria-hidden='true' />
                        <span>Close</span>
                    </button>
                </form>

                <div className='overflow-y-auto p-4 sm:p-6'>
                    <p className='sr-only' aria-live='polite'>
                        {data ? `${data.total} results` : ''}
                    </p>

                    {!data && !loading && (
                        <div>
                            <p className='font-bold text-navy'>
                                Popular searches
                            </p>

                            <div className='mt-3 flex flex-wrap gap-3'>
                                {POPULAR_SEARCHES.map(item => (
                                    <button
                                        key={item}
                                        type='button'
                                        onClick={() => setQuery(item)}
                                        className='min-h-12 rounded-full border-2 border-border bg-white px-5 font-semibold text-navy transition-colors hover:border-primary'
                                    >
                                        {item}
                                    </button>
                                ))}
                            </div>

                            <p className='mt-6 text-muted-foreground'>
                                Try a service, a window or door style, your town, or a question such as “how much do new windows cost”.
                            </p>
                        </div>
                    )}

                    {loading && !data && (
                        <p className='flex items-center gap-3 text-muted-foreground'>
                            <Loader2 className='size-5 animate-spin' aria-hidden='true' />
                            Searching...
                        </p>
                    )}

                    {failed && (
                        <p className='text-muted-foreground'>
                            Search is not responding right now. Please call us at (256) 585-6817, or try again in a moment.
                        </p>
                    )}

                    {data && (
                        <div className='flex flex-col gap-6'>
                            {data.interior && <InteriorAnswer onNavigate={onClose} />}

                            {data.total === 0 && !data.interior && (
                                <div>
                                    <p className='text-lg font-bold text-navy'>
                                        No matches for “{data.query}”.
                                    </p>

                                    <p className='mt-2'>
                                        Try a shorter word, or call us at <a href='tel:+12565856817' className='font-bold text-primary underline'>(256) 585-6817</a> and ask. We are glad to help.
                                    </p>
                                </div>
                            )}

                            {grouped.map(({ group, items }) => (
                                <section key={group} aria-label={group}>
                                    <h2 className='text-sm font-bold uppercase tracking-wider text-muted-foreground'>
                                        {group}
                                    </h2>

                                    <ul className='mt-2 flex flex-col gap-2'>
                                        {items.map(item => (
                                            <li key={item.id}>
                                                <Link
                                                    href={item.url}
                                                    onClick={() => choose(item.url)}
                                                    className='block rounded-xl border border-transparent bg-white p-4 transition-colors hover:border-primary focus-visible:border-primary'
                                                >
                                                    <span className='block text-lg font-bold text-navy'>
                                                        <Highlight text={item.title} terms={data.terms} />
                                                    </span>

                                                    <span className='mt-1 block text-base text-muted-foreground'>
                                                        <Highlight text={item.snippet} terms={data.terms} />
                                                    </span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            ))}

                            {data.total > 0 && (
                                <Link
                                    href={`/search/?q=${encodeURIComponent(data.query)}`}
                                    onClick={() => choose(`/search/?q=${data.query}`)}
                                    className='inline-flex items-center gap-2 self-start font-bold text-primary underline underline-offset-4'
                                >
                                    See all {data.total} results
                                    <ArrowRight className='size-5' aria-hidden='true' />
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default SearchDialog
